
// src/pages/admin/StoreManagementPage.js
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Modal, TextField,
    Switch, FormControlLabel, Alert, Select, MenuItem,
    InputLabel, FormControl, CircularProgress
} from '@mui/material';
import { DataGrid} from '@mui/x-data-grid';
import {  esES } from '@mui/x-data-grid/locales';
import api from '../../api/index.js';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 600 },
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflowY: 'auto'
};

const StoreManagementPage = () => {
    // --- ESTADOS ---
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({});
    const [editingStore, setEditingStore] = useState(null);
    const [error, setError] = useState('');

    // --- CARGA DE DATOS ---
    const fetchStores = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/stores?timestamp=${new Date().getTime()}`);
            setStores(response.data);
        } catch (err) {
            setError('No se pudieron cargar las tiendas.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStores();
    }, []);

    // --- MANEJADORES DE MODAL Y FORMULARIO ---
    const handleOpenCreate = () => {
        setEditingStore(null);
        setFormData({
            name: '',
            address: '',
            phone: '',
            is_active: true,
            sale_condition_type: 'NONE',
            sale_condition_amount: 0,
            inventory_source_store_id: '' // Valor inicial para la nueva opción
        });
        setError('');
        setOpenModal(true);
    };

    const handleOpenEdit = (store) => {
        setEditingStore(store);
        setFormData({
            name: store.name || '',
            address: store.address || '',
            phone: store.phone || '',
            is_active: store.is_active,
            sale_condition_type: store.sale_condition_type || 'NONE',
            sale_condition_amount: store.sale_condition_amount || 0,
            inventory_source_store_id: store.inventory_source_store_id || '' // Cargar valor existente o vacío
        });
        setError('');
        setOpenModal(true);
    };

    const handleCloseModal = () => setOpenModal(false);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async () => {
        setError('');
        try {
            if (editingStore) {
                await api.put(`/stores/${editingStore.id}`, formData);
            } else {
                await api.post('/stores', formData);
            }
            handleCloseModal();
            fetchStores();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar la tienda.');
        }
    };

    // --- COLUMNAS DE LA TABLA ---
    const columns = [
        { field: 'id', headerName: 'ID', width: 90 },
        { field: 'name', headerName: 'Nombre', width: 250 },
        { field: 'address', headerName: 'Dirección', flex: 1, minWidth: 200 },
        { field: 'phone', headerName: 'Teléfono', width: 150 },
        { field: 'is_active', headerName: 'Activa', type: 'boolean', width: 120 },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <Button size="small" variant="outlined" onClick={() => handleOpenEdit(params.row)}>
                    Editar
                </Button>
            )
        }
    ];

    // --- RENDERIZADO ---
    return (
        <Box sx={{ height: '85vh', width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Gestión de Tiendas</Typography>
                <Button variant="contained" onClick={handleOpenCreate}>Añadir Tienda</Button>
            </Box>

            {error && !loading && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <DataGrid
                rows={stores}
                columns={columns}
                loading={loading}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            />

            <Modal open={openModal} onClose={handleCloseModal}>
                <Box sx={style}>
                    <Typography variant="h6">{editingStore ? 'Editar Tienda' : 'Crear Tienda'}</Typography>
                    {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
                    
                    <TextField margin="normal" required fullWidth autoFocus label="Nombre" name="name" value={formData.name || ''} onChange={handleInputChange} />
                    <TextField margin="normal" fullWidth label="Dirección" name="address" value={formData.address || ''} onChange={handleInputChange} />
                    <TextField margin="normal" fullWidth label="Teléfono" name="phone" value={formData.phone || ''} onChange={handleInputChange} />

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Condición de Venta</InputLabel>
                        <Select
                            name="sale_condition_type"
                            value={formData.sale_condition_type || 'NONE'}
                            label="Condición de Venta"
                            onChange={handleInputChange}
                        >
                            <MenuItem value={'NONE'}>Sin Condición</MenuItem>
                            <MenuItem value={'GREATER_THAN'}>Total de Venta MAYOR A</MenuItem>
                            <MenuItem value={'LESS_THAN'}>Total de Venta MENOR A</MenuItem>
                        </Select>
                    </FormControl>

                    {formData.sale_condition_type && formData.sale_condition_type !== 'NONE' && (
                        <TextField
                            margin="normal" required fullWidth
                            label="Monto de la Condición" name="sale_condition_amount"
                            type="number" value={formData.sale_condition_amount || ''}
                            onChange={handleInputChange}
                            helperText="Ej: Para > 500, ingresa 500."
                        />
                    )}

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Fuente de Inventario (Opcional)</InputLabel>
                        <Select
                            name="inventory_source_store_id"
                            value={formData.inventory_source_store_id || ''}
                            label="Fuente de Inventario (Opcional)"
                            onChange={handleInputChange}
                        >
                            <MenuItem value="">
                                <em>Usar inventario propio (Esta es una tienda principal)</em>
                            </MenuItem>
                            {stores.map(s => (
                                editingStore?.id !== s.id && (
                                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                )
                            ))}
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={<Switch checked={!!formData.is_active} onChange={handleInputChange} name="is_active" />}
                        label="Tienda Activa"
                    />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseModal} sx={{ mr: 1 }}>Cancelar</Button>
                        <Button variant="contained" onClick={handleSubmit}>Guardar</Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default StoreManagementPage;